import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate, formatPatientId, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import {
    Alert,
    Box,
    Card,
    Checkbox,
    Collapse,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';

type SamePatientOrderListProps = {
    orders: Order[];
    checked: boolean;
    onChange: (checked: boolean) => void;
};
const SamePatientOrderList: FC<SamePatientOrderListProps> = ({
    orders,
    checked,
    onChange,
}: SamePatientOrderListProps) => {
    const { t: translate } = useTranslation(['orders', 'patient', 'common', 'restoration-types']);
    const [isNewOrder, setIsNewOrder] = useState<boolean>(false);
    const [showAnimation, setShowAnimation] = useState<boolean>(false);

    const navigateToOrderDetails = (orderId: string) => {
        window.open(`/order-details/${orderId}/main-view`, '_blank');
    };

    useEffect(() => {
        setShowAnimation(true);
    }, []);

    useEffect(() => {
        setIsNewOrder(checked);
    }, [checked]);

    const handleNewOrderChecked = (checked: boolean) => {
        setIsNewOrder(checked);
        onChange(checked);
    };

    return (
        <Fragment>
            {orders.length > 0 ? (
                <Fragment>
                    <Collapse in={showAnimation} timeout={800}>
                        <Box>
                            <Box mb={1} mt={1} display="flex">
                                <Box flexGrow={1}>
                                    <Alert severity="info">{translate('patient:EXISTING_ORDER_INFO')}</Alert>
                                </Box>
                            </Box>

                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('common:PATIENT')}</TableCell>
                                            <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                            <TableCell>{translate('common:DENTIST')}</TableCell>
                                            <TableCell>{translate('common:STATUS')}</TableCell>
                                            <TableCell>{translate('common:CREATED')}</TableCell>
                                            <TableCell>{translate('orders:DELIVERY_DATE')}</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {orders.map((order) => (
                                            <TableRow
                                                key={order._id}
                                                sx={{ cursor: 'pointer' }}
                                                hover
                                                onClick={() => navigateToOrderDetails(order._id)}
                                            >
                                                <TableCell className="mouseflow-mask">
                                                    <Box>{formatPatientId(order.patient)}</Box>
                                                    <Typography variant="caption">
                                                        {formatPatientName(
                                                            order.patient,
                                                            translate('orders:NO_PATIENT_REFERENCE'),
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {order.restorationTypes.map((type) => (
                                                        <Box key={type}>{translate(`restoration-types:${type}`)}</Box>
                                                    ))}
                                                </TableCell>
                                                <TableCell>
                                                    {order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}
                                                </TableCell>
                                                <TableCell>
                                                    <OrderStatusChip status={order.status} />
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(order.originalCreationDate, 'datetime')}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(new Date(order.deliveryDetails.date), 'datetime')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box mt={2}>
                                <Card variant="outlined" sx={{ maxWidth: 600, padding: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                onChange={(_, value) => handleNewOrderChecked(value)}
                                                checked={isNewOrder}
                                                size="large"
                                                data-test="same-patient-order-list.new-order-checkbox"
                                            />
                                        }
                                        label={
                                            <Typography variant="subtitle1">
                                                {translate('patient:THE_NEW_ORDER_IS_NOT_RELATED_TO_EXISTING_ORDER')}
                                            </Typography>
                                        }
                                    />
                                </Card>
                            </Box>
                        </Box>
                    </Collapse>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default SamePatientOrderList;
